import { useQuery } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { Wifi, WifiOff, Users, Images, Camera, RefreshCw } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { Card, StatCard } from '@/components/Card';
import { Badge } from '@/components/Badge';
import Loader, { EmptyState } from '@/components/Loader';
import { trackerService, fileUrl } from '@/services';
import { fmtTime, secondsToHours, fmtAgo, activityColor, initials } from '@/utils/format';

export default function TrackerLive() {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, isFetching, refetch } = useQuery({
    queryKey: ['tracker', 'live'],
    queryFn: trackerService.adminLive,
    refetchInterval: 15000,
  });

  const totals = data?.totals;
  const items = data?.items || [];
  const offline = data?.offline || [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Live Tracker"
        subtitle={`${dayjs(data?.date || undefined).format('dddd, DD MMMM YYYY')} · auto-refreshes every 15s`}
        actions={
          <>
            <Link to="/admin/tracker/screenshots" className="btn-secondary">
              <Images size={14} /> Screenshots
            </Link>
            <button className="btn-secondary" onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} /> Refresh
            </button>
          </>
        }
      />

      {isError ? (
        <Card>
          <EmptyState
            title="Could not load the live tracker"
            subtitle={error?.response?.data?.message || error?.message || 'Something went wrong'}
            action={<button className="btn-primary" onClick={() => refetch()}>Try again</button>}
          />
        </Card>
      ) : isLoading ? (
        <Loader />
      ) : (
        <>
          <div className="grid sm:grid-cols-3 gap-4">
            <StatCard icon={Wifi} color="emerald" label="Online now" value={totals?.online ?? 0} />
            <StatCard icon={Users} color="brand" label="Total employees" value={totals?.employees ?? 0} />
            <StatCard icon={WifiOff} color="rose" label="Offline" value={totals?.offline ?? 0} />
          </div>

          <Card>
            <div className="font-semibold mb-3 flex items-center gap-2">
              <Wifi size={18} /> Online employees
            </div>

            {items.length === 0 ? (
              <EmptyState
                title="Nobody is online right now"
                subtitle="The tracker desktop app is not reporting any active session. Sessions appear here as soon as an employee starts tracking."
              />
            ) : (
              <div className="table-wrap">
                <table className="min-w-full">
                  <thead>
                    <tr>
                      <th className="th">Employee</th>
                      <th className="th">Device</th>
                      <th className="th">Started</th>
                      <th className="th">Last seen</th>
                      <th className="th">Active today</th>
                      <th className="th w-48">Activity</th>
                      <th className="th">Shots</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => {
                      const pct = Math.round(it.today?.activityPercent ?? it.session?.activityPercent ?? 0);
                      return (
                        <tr
                          key={it.session?._id || it.user?._id}
                          className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50"
                          onClick={() => navigate(`/admin/tracker/${it.user?._id}`)}
                        >
                          <td className="td">
                            <div className="flex items-center gap-3">
                              {it.user?.avatar ? (
                                <img
                                  src={fileUrl(it.user.avatar)}
                                  alt={it.user?.name}
                                  className="w-9 h-9 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-500 to-indigo-500 text-white grid place-items-center font-semibold text-xs">
                                  {initials(it.user?.name)}
                                </div>
                              )}
                              <div className="min-w-0">
                                <div className="font-medium flex items-center gap-2">
                                  {it.user?.name}
                                  {it.idleNow && (
                                    <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                                      Idle
                                    </Badge>
                                  )}
                                  {/* A machine whose screen capture is broken used to be
                                      indistinguishable from one that had simply taken no
                                      screenshots yet. Now it says so, with the reason. */}
                                  {it.session?.captureOk === false && (
                                    <Badge
                                      title={it.session?.lastError || 'Screen capture is failing'}
                                      className="bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300"
                                    >
                                      Capture failing
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-xs text-slate-500">
                                  {it.user?.employeeId || it.user?.email}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="td">
                            <div className="text-sm">{it.session?.device?.hostname || '—'}</div>
                            <div className="text-xs text-slate-500">
                              {[it.session?.device?.platform, it.session?.device?.release].filter(Boolean).join(' · ') || '—'}
                            </div>
                          </td>
                          <td className="td whitespace-nowrap">{fmtTime(it.session?.startedAt)}</td>
                          <td className="td whitespace-nowrap text-slate-500">
                            {fmtAgo(it.session?.lastHeartbeatAt)}
                          </td>
                          <td className="td whitespace-nowrap font-medium">
                            {secondsToHours(it.today?.activeSeconds ?? 0)}
                          </td>
                          <td className="td">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden min-w-[80px]">
                                <div
                                  className={`h-full rounded-full ${activityColor(pct)}`}
                                  style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                                />
                              </div>
                              <span className="text-xs text-slate-500 w-9 text-right">{pct}%</span>
                            </div>
                          </td>
                          <td className="td">
                            <span
                              className={`inline-flex items-center gap-1 ${
                                it.session?.captureOk === false ? 'text-rose-600' : 'text-slate-500'
                              }`}
                              title={
                                it.session?.pendingUploads
                                  ? `${it.session.pendingUploads} screenshot(s) captured but not yet uploaded`
                                  : undefined
                              }
                            >
                              <Camera size={14} /> {it.today?.screenshots ?? 0}
                              {it.session?.pendingUploads > 0 && (
                                <span className="text-xs text-amber-600">
                                  (+{it.session.pendingUploads} queued)
                                </span>
                              )}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card>
            <div className="font-semibold mb-3 flex items-center gap-2">
              <WifiOff size={18} /> Offline employees
              <span className="text-sm font-normal text-slate-500">({offline.length})</span>
            </div>
            {offline.length === 0 ? (
              <EmptyState title="Everyone is online" subtitle="No offline employees right now." />
            ) : (
              <div className="table-wrap">
                <table className="min-w-full">
                  <thead>
                    <tr>
                      <th className="th">Employee</th>
                      <th className="th">Last seen</th>
                      <th className="th">Active today</th>
                      <th className="th">Shots today</th>
                    </tr>
                  </thead>
                  <tbody>
                    {offline.map((it) => (
                      <tr
                        key={it.user?._id}
                        className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50"
                        onClick={() => navigate(`/admin/tracker/${it.user?._id}`)}
                      >
                        <td className="td">
                          <div className="flex items-center gap-3">
                            {it.user?.avatar ? (
                              <img src={fileUrl(it.user.avatar)} alt={it.user?.name} className="w-9 h-9 rounded-full object-cover opacity-80" />
                            ) : (
                              <div className="w-9 h-9 rounded-full bg-slate-300 dark:bg-slate-700 text-white grid place-items-center font-semibold text-xs">
                                {initials(it.user?.name)}
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="font-medium">{it.user?.name}</div>
                              <div className="text-xs text-slate-500">{it.user?.employeeId || it.user?.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="td whitespace-nowrap text-slate-500">
                          {it.lastSeen ? fmtAgo(it.lastSeen) : <span className="text-slate-400">Never tracked</span>}
                        </td>
                        <td className="td whitespace-nowrap font-medium">
                          {it.today?.activeSeconds ? secondsToHours(it.today.activeSeconds) : '—'}
                        </td>
                        <td className="td">
                          <span className="inline-flex items-center gap-1 text-slate-500">
                            <Camera size={14} /> {it.today?.screenshots ?? 0}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
